import React, { useRef, useEffect } from "react"

import domCleanupManager from "../../../util/customview/DomCleanUpManager"
import { useAppDispatch, useAppSelector } from "../../../hooks"
import { GlobalState } from "../../../store"

import { IInitializeRootCauseAnalysis, initialize } from "../slice"
import { IRootCauseAnalysisActions } from ".."

import Controls from "./controls/Controls"
import Finding from "./findings/Finding"
import Separator from "../../common/Separator"

interface Props extends IInitializeRootCauseAnalysis {
    actions: IRootCauseAnalysisActions
}

export const RootCauseAnalysis = ({
    actions,
    ...restOfProps
}: Props): JSX.Element => {
    const dispatch = useAppDispatch()

    // ensure the RTF css bleeding is handled
    const containerRef = useRef<HTMLDivElement | null>(null)
    const cleanupHandleRef = useRef<DomCleanupHandle | null>(null)
    useEffect(() => {
        if (!containerRef.current) {
            return
        }

        const handle = domCleanupManager.register({
            element: containerRef.current,
        })

        cleanupHandleRef.current = handle

        // we initialize here, because with the DOM cleanup
        // there is a delay when the classes are cleaned up
        // causing the data to be loaded,
        // while the autogrow textarea is not adjusted to the full content yet
        dispatch(initialize(restOfProps))

        return (): void => {
            handle.unregister()
            cleanupHandleRef.current = null
        }
    }, [])

    const fieldsData = useAppSelector((state: GlobalState) => state.fields)

    useEffect(() => {
        if (fieldsData.isInitialized) {
            cleanupHandleRef.current?.cleanup()
        }
    }, [
        fieldsData.isInitialized,
        fieldsData.isLoading,
        fieldsData.currentFieldInEditMode,
    ])

    const { findings } = useAppSelector((state: GlobalState) => state.incidents)

    return (
        <div
            className="tw-flex tw-flex-col tw-gap-2"
            data-ft-component="incidentsRCA"
            ref={containerRef}
        >
            <div data-ft-component="children" data-ft-count={findings.length}>
                {findings.map((finding, index) => (
                    <React.Fragment key={finding.id}>
                        <Finding finding={finding} actions={actions} />
                        {index < findings.length - 1 && <Separator />}
                    </React.Fragment>
                ))}
            </div>

            <Controls actions={actions} />
        </div>
    )
}

export default RootCauseAnalysis
